import Header from "../../Common/header";
import Footer from "../../Common/footer";
import useDocumentTitle from "../../Common/useDocumentTitle";

/**
 * Returns the Terms & Conditions page component.
 *
 * @returns The Terms & Conditions page component.
 */
const TermsAndConditions = () => {
  useDocumentTitle("Terms & Conditions");
  return (
    <>
      <Header />

      <div className="relative py-16 px-4 sm:px-16 lg:px-48 bg-white overflow-hidden dark:bg-gray-900">
        <div className="bg-white border-t-4 border-blue-600 mx-auto max-w-5xl shadow-md dark:bg-gray-800">
          <div className="px-8 py-8">
            <h2 className=" text-gray-500 text-sm dark:text-gray-400">
              Arctic Research Foundation - Terms and Conditions
            </h2>

            <h1 className=" mt-4 text-gray-800 text-2xl font-bold dark:text-white">
              Arctic Research Foundation Terms of Use
            </h1>

            <div className=" mt-2 w-12 border-2 bg-blue-600 border-blue-600"></div>

            <h2 className=" mt-4 text-gray-500 text-sm dark:text-gray-400">
              Last Updated: December 2025
            </h2>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Introduction
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                These Terms and Conditions ("Terms") govern your relationship
                with the public website and private web platform accessible at:{" "}
                <a
                  href="https://www.arcticfocus.org/"
                  className="text-blue-600 dark:text-blue-400"
                  target="_blank">
                  https://arcticfocus.org
                </a>{" "}
                ("the "Site") operated by the Arctic Research Foundation ("ARF",
                "us", or "we"). Please read these Terms carefully before using
                the Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base font-semibold dark:text-gray-400">
                By browsing, visiting or otherwise interacting with the Site,
                you accept and agree to be bound and comply with these Terms and
                our Privacy Policy, found at{" "}
                <a
                  href="/terms-and-conditions"
                  className="text-blue-600 dark:text-blue-400"
                  target="_blank">
                  "Terms"
                </a>{" "}
                and{" "}
                <a
                  target="_blank"
                  href="/privacy-policy"
                  className="text-blue-600 dark:text-blue-400">
                  "Privacy Policy"
                </a>
                , incorporated herein by reference. If you do not agree to these
                Terms or the Privacy Policy, you must not access or use the
                Site.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Modifications to the Terms and to the Site
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We reserve the right in our sole discretion to revise and update
                these Terms from time to time. Any and all such modifications
                are effective immediately upon posting and apply to all access
                to and continued use of the Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                If required by law, we will provide notice of material changes
                to these Terms by posting a notice on the Site and/or sending
                you an email if you have consented to receiving communications
                from us. You agree to periodically review the Terms in order to
                be aware of any such modifications, and your continued use shall
                constitute your acceptance of these Terms.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The information and material on the Site, and the Site, may be
                changed, withdrawn or terminated at any time in our sole
                discretion without prior notice or liability, for any reason
                whatsoever. We will not be liable if, for any reason, all or any
                part of the Site is restricted to users or unavailable at any
                time or for any period.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Privacy
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF is committed to protecting the privacy of your personal
                information. By using the Site, you accept and agree to our
                <a
                  target="_blank"
                  href="/privacy-policy"
                  className="text-blue-600 dark:text-blue-400">
                  {" "}
                  Privacy Policy
                </a>
                , which also governs your use of the Site.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Communication
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                By creating an account to have access to and to use ARF's web
                platform, you agree that ARF has the ability to communicate with
                you via email for the purposes of (i) informing you that certain
                actions have occurred or (ii) informing you of any necessary
                action(s) that need to be addressed.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF email notifications from the web platform are not
                solicitations and are essential to maintaining a direct line of
                communication with its users. Therefore, all active users of the
                Site agree that by using the services and the Site, they also
                agree to receive email communications from us. You consent that
                all agreements, notices, disclosures, and other communications
                that we provide to you electronically will satisfy any legal
                requirement that such communications be in writing. You may
                withdraw your consent to receive communications from us
                electronically by stopping your use of the Site and contacting
                us.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Intellectual Property Rights
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The Site and its entire contents, features, and functionality,
                including but not limited to all information, data, software,
                text, images, designs, trademarks, logos, button icons,
                graphics, audio clips, video clips, digital downloads or
                computer code are owned by and are the property of ARF or its
                content suppliers and are protected by intellectual property
                laws, including without limitation, copyright, trademarks, and
                other intellectual property rights.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You agree not to infringe any of the title, ownership, or
                intellectual property rights of ARF or any content supplier. You
                are not permitted to modify copies of any materials from the
                Site, nor delete or alter any copyright, trademark, or other
                proprietary rights notices from copies of materials from the
                Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                While certain trademarks of third parties may be used by ARF
                under license, the display of third-party trademarks on the Site
                should not be taken to imply any relationship or license between
                ARF and the owner of the trademark or to imply that ARF endorses
                the wares, services or business of the owner of the said
                trademark.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You may only use the Site for your personal and non-commercial
                use, unless you have obtained express permission from ARF to
                access and use the Site for another purpose. You shall not
                directly or indirectly reproduce, compile for an internal
                database, distribute, modify, create derivative works of,
                publicly display, publicly perform, republish, download, store,
                or transmit any of the material on our Site, in any form or
                medium whatsoever, except:
              </p>
            </blockquote>

            <ol
              style={{ listStyleType: "lower-alpha" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                Your computer and browser may temporarily store or cache copies
                of materials being accessed and viewed;
              </li>

              <li className="mt-4 ml-24 px-4">
                A reasonable number of copies for personal use only may be
                printed, keeping any proprietary notices thereon, which may only
                be used for non-commercial and lawful personal use and not for
                further reproduction, publication, or distribution of any kind
                on any medium whatsoever; and
              </li>

              <li className="mt-4 ml-24 px-4">
                In the event social media platforms are linked to certain
                content on our Site, you may take such actions as our Site and
                such third-party social media platforms permit.
              </li>
            </ol>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Indigenous Knowledge and Community Data
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The Arctic Research Foundation is committed to promoting the
                values embodied in the OCAP® and CARE principles. All Indigenous
                Knowledge (IK), research results, and community-shared data
                contained within this database are collectively held and
                governed in accordance with these principles. Access to and use
                of this information must respect these principles at all times.
                No Indigenous or Traditional Knowledge on this site may be used
                without the express, informed consent of the Knowledge Holders.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Indigenous Knowledge (IK) and Indigenous Community Data include
                cultural, ecological, linguistic, and traditional knowledge, as
                well as research results shared by Inuit, First Nations, and
                Métis communities. IK is governed by Indigenous data sovereignty
                principles, including:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                OCAP® Principles: Ownership, Control, Access, and Possession
              </li>
              <li className="mt-4 ml-24 px-4">
                CARE Principles: Collective Benefit, Authority to Control,
                Responsibility, and Ethics
              </li>
            </ul>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Submissions (Your Content)
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We may, from time to time at our discretion, display content
                through the Site, including written pieces and images, submitted
                to us by users of the Site using an account (all such content,
                "your content"). In our sole discretion, we may, at any time and
                for any reason, revise, remove, unpublish, or otherwise
                disassociate any of your content from the Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Datasets, research results, and other materials contributed by
                researchers remain the intellectual property of the contributing
                researcher(s) or their institution. You retain ownership of your
                content, including any incorporated audio, photographs, or
                videos. The Arctic Research Foundation does not claim ownership
                over any such materials.
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                Unpublished research or data will not be treated as public
                domain.
              </li>
              <li className="mt-4 ml-24 px-4">
                ARF will maintain secure storage and ensure that data remains
                under Canadian jurisdiction whenever possible.
              </li>
              <li className="mt-4 ml-24 px-4">
                Contributor rights, including co-authorship and institutional
                approvals, will be recognized and respected.
              </li>
            </ul>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                By submitting, posting or displaying content on or through the
                Site, you grant us a worldwide, non-exclusive, royalty-free
                licence (with the right to sublicense) to use, copy, reproduce,
                process, adapt, modify, publish, transmit, display, and
                distribute such content in any and all media or distribution
                methods now known or later developed. This licence authorizes us
                to make your content available to the rest of the world and to
                let others do the same. You agree that this licence includes the
                right for ARF to provide, promote, and improve the Site and to
                make content submitted to or through the Site available to other
                companies, organizations or individuals for the syndication,
                broadcast, distribution, promotion, or publication of such
                content on other media and services, subject to these Terms.
                Such additional uses by ARF, or other companies, organizations
                or individuals, are made with no compensation paid to you with
                respect to the content that you submit, post, transmit, or
                otherwise make available through the Site, as the use of the
                Site by you is hereby agreed as being sufficient compensation
                for the content and grant of rights herein. You further waive
                any moral rights or other rights of authorship as a condition of
                submitting your content.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You represent and warrant (i) that you have, or have obtained,
                all rights, licences, consents, permissions, power and/or
                authority necessary to grant the rights granted herein for any
                content that you submit, post or display on or through the Site;
                and (ii) that nothing in your content is obscene, defamatory or
                otherwise unlawful.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You must also disclose to us any actual and potential conflict
                of interest arising in connection with your content.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We are not responsible or legally liable to any third party for
                your content or the accuracy of any of your content submitted by
                you or any other user of the Site. You hereby indemnify ARF to
                the extent of any breach of intellectual property rights by or
                through your use of the Site. All rights not otherwise claimed
                under the Terms or by ARF are hereby reserved.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Research Data Protections
            </h2>
            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF is committed to safeguarding research outputs, ensuring
                proper storage, and respecting contributor rights, including
                co-author and institutional approvals. Access to research data
                is restricted to authorized personnel only.{" "}
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Your Use of the Site and Account Set-Up and Security{" "}
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The security of your personal information is very important to
                us. We use physical, electronic, and administrative measures
                designed to secure your personal information from accidental
                loss and from unauthorized access, use, alteration, and
                disclosure.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Unfortunately, the transmission of information via the Internet
                is not completely secure. Although we do our best to protect
                your personal information, we cannot guarantee the security of
                your personal information transmitted to our Site. Any
                transmission of personal information is at your own risk. We are
                not responsible for circumvention of any privacy settings or
                security measures contained on the Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Some features or areas of the Site require you to create a user
                profile using the Site (an "Account"). It is a condition of your
                use of the Site that all the information you provide on the Site
                is correct, current, and complete.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We reserve the right at any time and from time to time, to
                disable or terminate your Account, any username, password, or
                other identifier, whether chosen by you or provided by us, in
                our sole discretion for any or no reason, including any
                violation of any provision of these Terms.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Any username, password, or any other piece of information chosen
                by you, or provided to you as part of our security procedures,
                must be treated as confidential, and you must not disclose it to
                any other person or entity. You understand and agree that should
                you be provided an Account, your Account is personal to you, and
                you agree not to provide any other person with access to the
                Site or any portion of it using your username, password, or
                other security information. You agree to notify us immediately
                of any unauthorized access to or use of your username or
                password or any other breach of security. You also agree to
                ensure that you log out of your Account at the end of each
                session. You are responsible for any password misuse or any
                unauthorized access.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Restrictions
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                As a condition of your access and use, you agree that you may
                use the Site only for lawful purposes and in accordance with
                these Terms.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You are specifically restricted from, directly or indirectly,
                all of the following:
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                Publishing any material on the Site that involves, provides or
                contributes any false, inaccurate or misleading information;
              </li>

              <li className=" ml-24 px-4">
                Selling, sublicensing and/or otherwise commercializing any
                material from the Site;
              </li>

              <li className=" ml-24 px-4">
                Using the Site in any way that is or may be damaging to the
                Site;
              </li>

              <li className=" ml-24 px-4">
                Engaging in activities which undermine the security of the Site
                or the integrity of the computer systems, networks and/or other
                electronics of ARF and/or of other users of the Site;
              </li>

              <li className=" ml-24 px-4">
                Using the Sites in any way that impacts user access to the Site;{" "}
              </li>

              <li className=" ml-24 px-4">
                Using the Site contrary to applicable laws and regulations, or
                in any way that may cause harm to the Site, or to any person(s)
                or business entity;
              </li>

              <li className=" ml-24 px-4">
                Engaging in any data mining, data harvesting, data extracting or
                any other similar activity in relation to the Site;
              </li>

              <li className=" ml-24 px-4">
                Using the Site to engage in any advertising or marketing; and{" "}
              </li>

              <li className="ml-24 px-4">
                Submitting or uploading any material to ARF's Site that is
                exploitive, obscene, harmful, threatening, abusive, harassing,
                hateful, defamatory, sexually explicit or pornographic, violent,
                inflammatory, or discriminatory based on race, sex, religion,
                nationality, disability, sexual orientation, or age or other
                such legally prohibited ground or be otherwise objectionable,
                such determination to be made in ARF's sole discretion.
              </li>
            </ul>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                We may terminate or suspend access to our Site immediately
                without prior notice for any reason whatsoever, including,
                without limitation, for the foregoing reasons.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Site Monitoring and Enforcement, Suspension and TerminationSite
              Monitoring and Enforcement, Suspension and Termination
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF has the right, without provision of notice to:{" "}
              </p>
            </blockquote>

            <ul
              style={{ listStyleType: "disc" }}
              className="dark:text-gray-400">
              <li className="mt-4 ml-24 px-4">
                Remove or refuse to post on the Site any of your content for any
                or no reason in our sole discretion
              </li>

              <li className=" ml-24 px-4">
                At all times, take such actions with respect to any content
                deemed necessary or appropriate in our sole discretion,
                including, without limitation, for violating the Terms;
              </li>

              <li className=" ml-24 px-4">
                Take appropriate legal action, including, without limitation,
                referring the matter to law enforcement or a regulatory
                authority, or notifying the harmed party of any illegal or
                unauthorized use of the Site. Without limiting the foregoing, we
                have the right to fully cooperate with any law enforcement
                authorities or court order requesting or directing us to
                disclose the identity or other information of anyone posting any
                materials on or through the Site; and
              </li>

              <li className=" ml-24 px-4">
                Terminate or suspend your access to all or part of the Site for
                any or no reason, including, without limitation, any violation
                of these Terms.
              </li>
            </ul>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              No Reliance
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The content on the Site is provided for general information
                purposes only. It is not intended to amount to advice on which
                you should rely. You must obtain more specific or professional
                advice before taking, or refraining from, any action or inaction
                on the basis of the content on the Site.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Although we make reasonable efforts to monitor and update the
                information on our Site, we make no representations, warranties
                or guarantees, whether express or implied, that the content on
                our Site is accurate, complete or up to date. Your use of the
                Site is at your own risk.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Third-Party Sites
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The content may include links and interactive features to other
                websites and/or social media platforms that are not part of the
                Site. You acknowledge that your access or use of those websites
                may be subject to such policies, practices, and terms as their
                operators may impose and that we make no representations about
                any other websites that may be accessed from the Site. If you
                choose to access any such sites, you do so at your own risk. We
                have no control over the contents of any such third-party sites
                and accept no responsibility for such sites or for any loss or
                damage that may arise from your use of them. You are subject to
                any terms and conditions of such third-party sites.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Links to third-party sites from the Site may include links to
                certain social media features that enable you to link to or
                transmit content from the Site on your own or via certain
                third-party websites. You may only use these features when they
                are provided by us and solely with respect to the content
                identified.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Disclaimer of Warranties
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base font-bold dark:text-gray-400">
                You understand and agree that your use of the Site, its content,
                and any services or items found or attained through the Site is
                at your own risk. The Site, its content, and any services or
                items found or attained through the Site are provided on an "as
                is" and "as available" basis, without any warranties of any
                kind, either express or implied, including, but not limited to,
                the implied warranties of merchantability, fitness for a
                particular purpose, or non-infringement. The foregoing does not
                affect any warranties that cannot be excluded or limited under
                applicable law.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base font-bold dark:text-gray-400">
                Neither ARF nor its affiliates nor their respective directors,
                officers, employees, agents, service providers, contractors,
                licensors, licensees, suppliers or successors make any warranty,
                representation or endorsement with respect to the completeness,
                security, reliability, suitability, accuracy, currency or
                availability of the Site or its contents. Without limiting the
                foregoing, neither ARF nor its affiliates or their respective
                directors, officers, employees, agents, service providers,
                contractors, licensors, licensees, suppliers or successors
                represent or warrant that the Site, its content, or any services
                or items found or attained through the Site will be accurate,
                reliable, error-free, or uninterrupted, that defects will be
                corrected, that the Site or the server that makes them available
                are free of viruses or other harmful components.
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base font-bold dark:text-gray-400">
                We cannot and do not guarantee or warrant that files or data
                available for downloading from the Internet or the Site will be
                free of viruses or other destructive code. You are solely and
                entirely responsible for your use of the Site and your computer,
                Internet and data security. To the fullest extent provided by
                law, we will not be liable for any loss or damage caused by
                denial-of-service attack, distributed denial-of-service attack,
                overloading, flooding, mailbombing or crashing, viruses, trojan
                horses, worms, logic bombs, or other technologically harmful
                material that may infect your computer equipment, computer
                programs, data, or other proprietary material due to your use of
                the Site or any services or items found or attained through the
                Site or to your downloading of any material posted on it, or on
                any website linked to it.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Limitation of Liability
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base font-bold dark:text-gray-400">
                Except where such exclusions are prohibited by law, under no
                circumstance will ARF, its affiliates or their respective
                directors, officers, employees, agents, service providers,
                contractors, licensors, licensees, suppliers, or successors be
                liable for negligence, gross negligence, negligent
                misrepresentation, fundamental breach, damages of any kind,
                under any legal theory, including any direct, indirect, special,
                incidental, consequential, or punitive damages, including, but
                not limited to, personal injury, pain and suffering, emotional
                distress, loss of revenue, loss of profits, loss of business or
                anticipated savings, loss of use, loss of goodwill, loss of
                data, and whether caused by tort (including negligence), breach
                of contract, breach of privacy or otherwise, even if the party
                was allegedly advised or had reason to know, arising out of or
                in connection with your use, or inability to use, or reliance
                on, the Site, any linked websites or such other third-party
                websites, nor any of the Site content, materials, posting or
                information thereon even if the party was allegedly advised or
                had reason to know.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Indemnification
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                You hereby indemnify, defend and hold harmless ARF and its
                affiliates, and their respective directors, officers, employees,
                agents, service providers, contractors, licensors, licensees,
                suppliers and successors, to the fullest extent permitted by
                applicable law, from and against any and/or all claims, losses,
                liabilities, costs, demands, causes of action, damages or
                expenses, including reasonable legal fees and expenses, arising
                in any way related to your use of the Site or from your breach
                of any of these Terms.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Waiver
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                No failure to exercise, or delay in exercising, any right,
                remedy, power or privilege arising from these Terms operates, or
                may be construed, as a waiver thereof. No single or partial
                exercise of any right, remedy, power or privilege hereunder
                precludes any other or further exercise thereof or the exercise
                of any other right, remedy, power or privilege.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Assignment
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                ARF is allowed to assign, transfer, and subcontract its rights
                and/or obligations under these Terms without any notification.
                You may not assign, transfer, or subcontract any of your rights
                and/or obligations under these Terms.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Severability
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                If any term or provision of these Terms is invalid, illegal or
                unenforceable in any jurisdiction, such invalidity, illegality
                or unenforceability shall not affect any other term or provision
                of these Terms or invalidate or render unenforceable such term
                or provision in any other jurisdiction.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Entire Agreement
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                These Terms constitute the sole and entire agreement between ARF
                and you in relation to your use of the Site, and supersede all
                prior agreements and understandings.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Governing Laws {"&"} Jurisdiction
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                The Site and these Terms will be governed by, and interpreted
                and enforced in accordance with, the laws of the Province of
                Ontario and the federal laws of Canada, as applicable.
              </p>
            </blockquote>

            <h2 className="mt-4 text-blue-800 text-xl font-bold dark:text-white">
              Contact Us
            </h2>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                Should you become aware of misuse of the Site, including
                libellous or defamatory conduct, please report it to ARF at{" "}
                <a href="mailto:info@arcticresearchfoundation.ca">
                  info@arcticresearchfoundation.ca
                </a>
                .{" "}
              </p>
            </blockquote>

            <blockquote>
              <p className="mt-4 text-gray-800 text-base dark:text-gray-400">
                All other feedback, comments, requests for technical support,
                and other communications relating to these Terms or the Site
                should be directed to{" "}
                <a href="mailto:info@arcticresearchfoundation.ca">
                  info@arcticresearchfoundation.ca
                </a>
                .{" "}
              </p>
            </blockquote>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TermsAndConditions;
